import React, { useState, useEffect } from 'react';
import ClientOnboarding from './ClientOnboarding';
import { API_BASE } from '../config/apiConfig';

/**
 * UpdateClient — Renders the full onboarding wizard in "edit mode".
 *
 * In subscriber mode: auto-loads the client's existing data from the backend
 * and renders ClientOnboarding with editMode prop, allowing the subscriber
 * to update any step (Company Info, Database, Schema, GenAI, PDFs, etc.)
 *
 * In admin mode: shows a client ID lookup first, then loads the wizard.
 */
const UpdateClient = ({ clientConfig, subscriberMode, onComplete, onLaunchApp }) => {
  const [clientId, setClientId] = useState(clientConfig?.client_id || '');
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Auto-load client data in subscriber mode
  useEffect(() => {
    if (subscriberMode) {
      if (clientConfig?.client_id && clientConfig.client_id.startsWith('client_')) {
        // Valid provisioned client_id — fetch from backend
        loadClientData(clientConfig.client_id);
      } else {
        // No valid client_id yet (new signup, not yet provisioned after payment)
        // Build editData directly from clientConfig/session so the form opens immediately
        setEditData({
          client_id: clientConfig?.client_id || '',
          company_name: clientConfig?.company_name || '',
          ein: '',
          location: clientConfig?.location || '',
          contact_email: clientConfig?.contact_email || '',
          website_url: '',
          contact_phone: '',
          product_name: clientConfig?.product_name || '',
          product_type: clientConfig?.product_type || '',
          plan: clientConfig?.plan || 'growth',
          tenant_type: clientConfig?.tenant_type || 'standalone',
          tenant_role: clientConfig?.tenant_role || 'subscriber',
          parent_tenant_id: '',
          franchise_or_retail_subscription: 'retail_subscription',
          db_config: null,
          schema_metadata: null,
        });
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadClientData = async (id) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/clients/${id}/status`);
      if (res.ok) {
        const data = await res.json();
        setEditData({
          client_id: id,
          company_name: data.company_name || clientConfig?.company_name || '',
          ein: data.ein || '',
          location: data.location || '',
          contact_email: data.contact_email || '',
          website_url: data.website_url || '',
          contact_phone: data.contact_phone || '',
          product_name: data.product_name || clientConfig?.product_name || '',
          product_type: data.product_type || clientConfig?.product_type || '',
          plan: data.plan || clientConfig?.plan || 'growth',
          tenant_type: data.tenant_type || clientConfig?.tenant_type || 'standalone',
          tenant_role: data.tenant_role || clientConfig?.tenant_role || 'subscriber',
          parent_tenant_id: data.parent_tenant_id || '',
          franchise_or_retail_subscription: data.franchise_or_retail_subscription || 'retail_subscription',
          db_config: data.db_config || null,
          schema_metadata: data.schema_metadata || null,
        });
      } else {
        // Backend may not have the status endpoint — use clientConfig as fallback
        setEditData({
          client_id: id,
          company_name: clientConfig?.company_name || '',
          ein: '',
          location: '',
          contact_email: '',
          website_url: '',
          contact_phone: '',
          product_name: clientConfig?.product_name || '',
          product_type: clientConfig?.product_type || '',
          plan: clientConfig?.plan || 'growth',
          tenant_type: clientConfig?.tenant_type || 'standalone',
          tenant_role: clientConfig?.tenant_role || 'subscriber',
          parent_tenant_id: '',
          franchise_or_retail_subscription: 'retail_subscription',
          db_config: null,
          schema_metadata: null,
        });
      }
    } catch {
      // Fallback to clientConfig
      setEditData({
        client_id: id,
        company_name: clientConfig?.company_name || '',
        ein: '',
        location: '',
        contact_email: '',
        website_url: '',
        contact_phone: '',
        product_name: clientConfig?.product_name || '',
        product_type: clientConfig?.product_type || '',
        plan: clientConfig?.plan || 'growth',
        tenant_type: clientConfig?.tenant_type || 'standalone',
        tenant_role: clientConfig?.tenant_role || 'subscriber',
        parent_tenant_id: '',
        franchise_or_retail_subscription: 'retail_subscription',
        db_config: null,
        schema_metadata: null,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLookup = () => {
    if (clientId.trim()) {
      loadClientData(clientId.trim());
    } else {
      setError('Enter a client ID');
    }
  };

  // If edit data is loaded, render the onboarding wizard in edit mode
  if (editData) {
    return (
      <ClientOnboarding
        onComplete={(config) => {
          // After update completes (step 7), navigate to View Client or Launch App
          if (onComplete) {
            onComplete(config);
          }
        }}
        onLaunchApp={onLaunchApp}
        editMode={editData}
        subscriberMode={subscriberMode ? {
          locked: true,
          company_name: editData.company_name,
          email: editData.contact_email,
          product_type: editData.product_type,
          product_name: editData.product_name,
          plan: editData.plan || 'growth',
          tenant_role: 'subscriber',
          tenant_type: 'standalone',
          payment_validated: clientConfig?.payment_validated || false,
        } : undefined}
      />
    );
  }

  // Loading state
  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
        <p style={{ color: '#666' }}>Loading client data...</p>
      </div>
    );
  }

  // Admin mode: show lookup form
  if (!subscriberMode) {
    const fieldStyle = {
      width: '100%', padding: '10px 14px', border: '1px solid #ddd',
      borderRadius: '8px', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box',
    };
    return (
      <div style={{ padding: '24px', maxWidth: '600px', margin: '0 auto' }}>
        <h2 style={{ marginBottom: '4px' }}>✏️ Update Client</h2>
        <p style={{ color: '#666', marginBottom: '20px' }}>Look up a client by ID to update their settings</p>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          <input
            type="text" placeholder="Enter client ID (e.g. client_prab_001)"
            value={clientId} onChange={e => setClientId(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleLookup(); }}
            style={{ ...fieldStyle, marginBottom: 0, flex: 1 }}
            aria-label="Client ID"
          />
          <button onClick={handleLookup} style={{
            padding: '10px 24px', background: '#2196F3', color: '#fff', border: 'none',
            borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
          }}>Look Up</button>
        </div>

        {error && <p style={{ color: '#f44336', marginBottom: '12px' }}>❌ {error}</p>}
      </div>
    );
  }

  // Subscriber mode but no data yet — show brief loading
  return (
    <div style={{ padding: '24px', textAlign: 'center' }}>
      <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
      <p style={{ color: '#666' }}>Preparing your client settings...</p>
      {error && <p style={{ color: '#f44336', marginTop: '12px' }}>❌ {error}</p>}
    </div>
  );
};

export default UpdateClient;
